import { ChangeDetectionStrategy, Component, output } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  UntypedFormControl,
  Validators,
} from '@angular/forms';
import {
  BdExportAsFileDialogFormHubspotContact,
  BdExportAsFileDialogHubspotContactData,
} from './hubspot-contact';
import { InputText } from 'primeng/inputtext';
import { IftaLabel } from 'primeng/iftalabel';
import { Checkbox } from 'primeng/checkbox';
import { Message } from 'primeng/message';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'bd-export-as-file-dialog-form-hubspot-contact',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <form [formGroup]="form">
      <div class="flex flex-col gap-4">
        <p-iftalabel>
          <input
            id="hs-company"
            pInputText
            type="text"
            fluid
            variant="filled"
            formControlName="company"
          />
          <label for="hs-company">Firma</label>
        </p-iftalabel>

        <div class="flex flex-row gap-6">
          <div class="flex-1 flex flex-col gap-1">
            <p-iftalabel>
              <input
                id="hs-firstname"
                pInputText
                type="text"
                fluid
                variant="filled"
                formControlName="firstname"
                [invalid]="isInvalid('firstname')"
                aria-describedby="hs-firstname-help"
              />
              <label for="hs-firstname">Vorname</label>
            </p-iftalabel>
            @if (isInvalid('firstname')) {
              <p-message
                severity="error"
                variant="simple"
                size="small"
                id="hs-firstname-help"
              >
                Bitte geben Sie ihren Vornamen an
              </p-message>
            }
          </div>

          <div class="flex-1 flex flex-col gap-1">
            <p-iftalabel>
              <input
                id="hs-lastname"
                pInputText
                type="text"
                fluid
                variant="filled"
                formControlName="lastname"
                [invalid]="isInvalid('lastname')"
                aria-describedby="hs-lastname-help"
              />
              <label for="hs-lastname">Nachname</label>
            </p-iftalabel>
            @if (isInvalid('lastname')) {
              <p-message
                severity="error"
                variant="simple"
                size="small"
                id="hs-lastname-help"
              >
                Bitte geben Sie ihren Nachnamen an
              </p-message>
            }
          </div>
        </div>

        <div class="flex flex-col gap-1">
          <p-iftalabel>
            <input
              id="hs-email"
              pInputText
              type="email"
              fluid
              variant="filled"
              formControlName="email"
              [invalid]="isInvalid('email')"
              aria-describedby="hs-email-help"
            />
            <label for="hs-email">Email</label>
          </p-iftalabel>
          @if (isInvalid('email')) {
            <p-message
              severity="error"
              variant="simple"
              size="small"
              id="hs-email-help"
            >
              @if (form.controls.email.hasError('required')) {
                Bitte geben Sie eine Email an
              } @else {
                Ungültige Email-Adresse
              }
            </p-message>
          }
        </div>

        <div class="basis-1/2 flex flex-col gap-1">
          <p-iftalabel>
            <input
              id="hs-mobilephone"
              pInputText
              type="tel"
              fluid
              variant="filled"
              formControlName="mobilephone"
              [invalid]="isInvalid('mobilephone')"
              aria-describedby="hs-mobilephone-help"
            />
            <label for="hs-mobilephone">Telefon</label>
          </p-iftalabel>
          @if (isInvalid('mobilephone')) {
            <p-message
              severity="error"
              variant="simple"
              size="small"
              id="hs-mobilephone-help"
            >
              @if (form.get('mobilephone').hasError('minlength')) {
                Mindestens 7 Stellen
              } @else if (form.get('mobilephone').hasError('maxlength')) {
                Maximal 20 Stellen
              } @else {
                Ungültige Nummer
              }
            </p-message>
          }
        </div>

        <p>
          BinDoc verpflichtet sich, Ihre Privatsphäre zu schützen und zu
          respektieren. Wir verwenden Ihre persönlichen Daten nur zur Verwaltung
          Ihres Kontos und zur Bereitstellung der von Ihnen angeforderten
          Produkte und Dienstleistungen. Von Zeit zu Zeit möchten wir Sie über
          unsere Produkte und Dienstleistungen sowie andere Inhalte, die für Sie
          von Interesse sein könnten, informieren. Wenn Sie damit einverstanden
          sind, dass wir Sie zu diesem Zweck kontaktieren, geben Sie bitte unten
          an, wie Sie von uns kontaktiert werden möchten:
        </p>

        <div class="flex items-start gap-2">
          <p-checkbox
            inputId="hs-privacy"
            [binary]="true"
            formControlName="privacyConsent"
          ></p-checkbox>
          <label for="hs-privacy"
            >Ich stimme zu, andere Benachrichtigungen von BinDoc zu
            erhalten.</label
          >
        </div>

        <p>
          Sie können diese Benachrichtigungen jederzeit abbestellen. Weitere
          Informationen zum Abbestellen, zu unseren Datenschutzverfahren und
          dazu, wie wir Ihre Privatsphäre schützen und respektieren, finden Sie
          in unserer Datenschutzrichtlinie.
        </p>

        <p>
          Indem Sie unten auf „Export anfordern" klicken, stimmen Sie zu, dass
          BinDoc die oben angegebenen persönlichen Daten speichert und
          verarbeitet, um Ihnen die angeforderten Inhalte bereitzustellen.
        </p>
      </div>
    </form>
  `,
  imports: [ReactiveFormsModule, InputText, IftaLabel, Checkbox, Message],
})
export class BdExportAsFileDialogHubspotContactComponent {
  public readonly contactChange = output<BdExportAsFileDialogFormHubspotContact>();

  public readonly form = new FormGroup({
    email: new FormControl<string>(null, [
      Validators.email,
      Validators.required,
    ]),
    firstname: new FormControl<string>(null, [Validators.required]),
    lastname: new FormControl<string>(null, [Validators.required]),
    company: new FormControl<string>(null, []),
    mobilephone: new UntypedFormControl(null, [
      Validators.minLength(7),
      Validators.maxLength(20),
    ]),
    privacyConsent: new FormControl<boolean>(true, [Validators.requiredTrue]),
  });

  constructor() {
    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      const updated = new BdExportAsFileDialogFormHubspotContact(
        value as BdExportAsFileDialogHubspotContactData,
        this.form.valid,
      );
      this.contactChange.emit(updated);
    });
  }

  /** True once the control is invalid and the user has interacted with it (styleguide: show errors on touch). */
  public isInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!control && control.invalid && control.touched;
  }
}
